import React, { useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Text,
  Textarea,
  Checkbox,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
// import classes from './Socius.module.css';
import { joinEarlyAccess } from "../api";
import { useNavigate } from "react-router-dom"; // Import useNavigate

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate(); // Initialize navigate

  const form = useForm({
    initialValues: {
      email: "",
      name: "",
      phone: "",
      title: "",
      company: "",
      message: "",
      email_consent: false,
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      email_consent: (value) =>
        value === true ? null : "You must consent to receiving emails.",
    },
  });

  const handleSubmit = async (values: {
    email: string;
    name: string;
    phone: string;
    title: string;
    company: string;
    message: string;
    email_consent: boolean;
  }) => {
    if (!values.email_consent) {
      form.setFieldError(
        "email_consent",
        "You must consent to receiving emails.",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await joinEarlyAccess(values);

      if (response.status === "success") {
        form.reset();
        onClose();
        if (response.message.includes("already on our early access list")) {
          notifications.show({
            title: "Already Registered",
            message: response.message,
            color: "blue",
          });
        } else {
          navigate("/success"); // Redirect to the success page for new submissions
        }
      } else {
        throw new Error("Unexpected response");
      }
    } catch (error) {
      notifications.show({
        title: "Submission Failed",
        message:
          "We were unable to process your request at this time. Please try again later or contact support@sociuslegal.com",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Connect with Socius Legal"
      size="lg"
      zIndex={1000}
      centered
      styles={{
        title: {
          fontWeight: "500",
          fontSize: "1.2rem",
        },
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          // classNames={classes}
          required
          label="Email"
          placeholder="your.email@example.com"
          {...form.getInputProps("email")}
        />
        <TextInput
          // classNames={classes}
          label="Name"
          placeholder="John Doe"
          mt="md"
          {...form.getInputProps("name")}
        />
        <TextInput
          // classNames={classes}
          label="Phone Number"
          placeholder="+1 (555) 123-4567"
          mt="md"
          {...form.getInputProps("phone")}
        />
        <TextInput
          // classNames={classes}
          label="Job Title"
          placeholder="Senior Partner"
          mt="md"
          {...form.getInputProps("title")}
        />
        <TextInput
          // classNames={classes}
          label="Company"
          placeholder="Law Firm LLC"
          mt="md"
          {...form.getInputProps("company")}
        />
        <Textarea
          // classNames={classes}
          label="How can we help? (Optional)"
          placeholder="Tell us about your specific needs or questions"
          mt="md"
          {...form.getInputProps("message")}
        />
        <Checkbox
          required
          label="I consent to receive emails from Socius Legal (we will never share your email address with anyone)"
          mt="md"
          {...form.getInputProps("email_consent", { type: "checkbox" })}
        />
        {form.errors.email_consent && (
          <Text color="red" size="sm" mt="xs">
            {form.errors.email_consent}
          </Text>
        )}
        <Group mt="xl" justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={!form.values.email_consent || isSubmitting}
          >
            Submit
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
